import {
  Button,
  Center,
  HStack,
  Input,
  SegmentedControl,
  SegmentedControlItem,
  Typography,
  VStack,
  XelaColor,
  XelaGradient,
} from '@/XelaReact'

import { useForm } from '@inertiajs/react'
import Guest from '@/Layouts/Guest'
import XLink from '@/Components/XLink'
import { IconArrowRight, IconEye, IconEyeOff, IconLock, IconMail, IconPhone } from '@tabler/icons-react'
import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import useLocation from '@/Hooks/useLocation'
import XPhoneInput from '@/Mantine/XPhoneInput'
import useMeta from '@/Hooks/useMeta'

interface FormProps extends Record<string, unknown> {
  email: string
  password: string
  phone: string
  otp: string
  country_code: string
  country_calling_code: string
}

const Login: FC = () => {
  const meta = useMeta()
  const location = useLocation()

  const [emailPhoneSwitcher, setEmailPhoneSwitcher] = useState('email')
  const [showPassword, setShowPassword] = useState(false)
  const [showSecureOTPInput, setShowSecureOTPInput] = useState(false)

  const [showOTPInput, setShowOTPInput] = useState(false)

  const passwordRef = useRef<HTMLInputElement | null>(null)

  const { data, setData, post, errors, recentlySuccessful, reset, clearErrors } = useForm<FormProps>({
    email: '',
    password: '',
    phone: '',
    otp: '',
    country: 'India',
    country_code: 'IN',
    country_calling_code: '+91',
  })

  useEffect(() => {
    setData((prev) => {
      return {
        ...prev,
        country: location['country_name'],
        country_code: location['country_code'],
        country_calling_code: location['country_calling_code'],
      }
    })
  }, [location])

  useEffect(() => {
    if (errors.otp === 'otp-sent') {
      setShowSecureOTPInput(true)
    } else {
      if (errors.otp === 'otp-required') {
        setShowSecureOTPInput(true)
      }
    }
  }, [errors.otp])

  useEffect(() => {
    if (errors.password) {
      passwordRef.current?.focus()
    }
  }, [errors.password])

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault()

        if (emailPhoneSwitcher === 'phone') {
          if (showOTPInput) {
            post(route('login', { type: emailPhoneSwitcher }))
          } else {
            post(route('login.user-exists', { type: emailPhoneSwitcher }))
          }
        } else {
          post(route('login', { type: emailPhoneSwitcher }))
        }
      }
    }

    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [data])

  const onLoginHandle = (name: string, value: string) => {
    setData((prev) => {
      const datum: { [key: string]: string } = {}
      datum[name] = value

      return {
        ...prev,
        ...datum,
      }
    })
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()

    if (emailPhoneSwitcher === 'phone') {
      if (showOTPInput) {
        post(route('login', { type: emailPhoneSwitcher }))
      } else {
        post(route('login.user-exists', { type: emailPhoneSwitcher }))
      }
    } else {
      post(route('login', { type: emailPhoneSwitcher }))
    }
  }

  useEffect(() => {
    if (recentlySuccessful) {
      if (emailPhoneSwitcher === 'phone') {
        setShowOTPInput(true)
      }
    }
  }, [recentlySuccessful])

  return (
    <Guest>
      <VStack alignItems="center">
        <VStack
          spacing="24px"
          width="auto"
          bg={XelaColor.White}
          borderRadius="32px"
          style={{ padding: '24px 48px', margin: '24px', minWidth: '440px', minHeight: '484px' }}
        >
          <VStack alignItems="center" spacing="8px">
            <Typography variant="title3">Sign In</Typography>
            <Typography color={XelaColor.Gray6} variant="body-small-bold">
              To Access {meta.appName} Home
            </Typography>
          </VStack>
          <Center>
            <SegmentedControl
              items={[
                new SegmentedControlItem('email', 'Email', <IconMail stroke={1} />),
                new SegmentedControlItem('phone', 'Phone Number', <IconPhone stroke={1} />),
              ]}
              activeItemBg={XelaGradient(XelaColor.Green3, XelaColor.Blue3)}
              defaultValue="email"
              name={'email-phone-selector'}
              onChangeHandle={(value) => {
                setEmailPhoneSwitcher(value)
                setShowSecureOTPInput(false)
                clearErrors()
                reset()
              }}
            />
          </Center>
          {emailPhoneSwitcher === 'email' && (
            <VStack spacing="12px">
              <VStack spacing="10px">
                <Input
                  error={!!errors.email}
                  name="email"
                  type="text"
                  placeholder="Email Address"
                  value={data.email}
                  onChangeHandle={(value) => onLoginHandle('email', value)}
                  rightElement={<IconMail color={XelaColor.Gray3} stroke={1} />}
                  helperText={
                    errors.email !== '' ? (errors.email !== 'email-not-exists' ? errors.email : undefined) : undefined
                  }
                />
                {errors.email === 'email-not-exists' && (
                  <Typography as="p" color={XelaColor.Red3} variant="caption" style={{ width: '344px' }}>
                    This account cannot be found.
                  </Typography>
                )}
              </VStack>
              <Input
                inputRef={passwordRef}
                className="transition"
                error={!!errors.password}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={data.password}
                placeholder="Password"
                onChangeHandle={(value: string) => onLoginHandle('password', value)}
                rightElement={
                  data.password.length > 0 ? (
                    showPassword ? (
                      <IconEyeOff
                        style={{ cursor: 'pointer' }}
                        color={XelaColor.Gray3}
                        stroke={1}
                        onClick={() => {
                          setShowPassword(false)
                        }}
                      />
                    ) : (
                      <IconEye
                        style={{ cursor: 'pointer' }}
                        color={XelaColor.Gray3}
                        stroke={1}
                        onClick={() => {
                          setShowPassword(true)
                        }}
                      />
                    )
                  ) : (
                    <IconLock color={XelaColor.Gray3} stroke={1} />
                  )
                }
                helperText={errors.password !== '' ? errors.password : undefined}
              />
              {showSecureOTPInput && (
                <Input
                  className="transition"
                  error={!!errors.otp}
                  name="otp"
                  type="number"
                  value={data.otp}
                  placeholder="OTP"
                  onChangeHandle={(value) => onLoginHandle('otp', value)}
                  helperText={
                    errors.otp
                      ? errors.otp === 'otp-sent'
                        ? 'OTP sent to your mobile'
                        : errors.otp === 'otp-required'
                        ? 'Please provide otp sent to mobile'
                        : errors.otp
                      : undefined
                  }
                />
              )}

              <Button onClick={submit} autoresize={false} label={'Sign In'} rightIcon={<IconArrowRight stroke={1} />} />
              <Center>
                <Typography variant="body-small">
                  <XLink href={route('password.request')}>Forgot Password?</XLink>
                </Typography>
              </Center>
            </VStack>
          )}
          {emailPhoneSwitcher === 'phone' && (
            <VStack spacing="12px">
              <VStack spacing="10px">
                <XPhoneInput
                  error={
                    errors.phone !== '' ? (errors.phone !== 'phone-not-exists' ? errors.phone : undefined) : undefined
                  }
                  dialCode={data.country_calling_code}
                  countryCode={data.country_code}
                  initialValue={data.phone}
                  onChange={(number, dialCode, countryCode, country) => {
                    setData((prevData) => ({
                      ...prevData,
                      phone: number,
                      country_calling_code: dialCode,
                      country_code: countryCode,
                      country: country,
                    }))
                  }}
                ></XPhoneInput>
                {errors.phone === 'phone-not-exists' && (
                  <Typography as="p" color={XelaColor.Red3} variant="caption" style={{ width: '344px' }}>
                    This account cannot be found.
                  </Typography>
                )}
              </VStack>
              {showOTPInput && (
                <Input
                  className="transition"
                  error={!!errors.otp}
                  name="otp"
                  type="number"
                  value={data.otp}
                  placeholder="OTP"
                  onChangeHandle={(value) => onLoginHandle('otp', value)}
                  helperText={errors.otp !== '' ? errors.otp : undefined}
                />
              )}
              <Button
                onClick={submit}
                autoresize={false}
                label={showOTPInput ? 'Sign In' : 'Send OTP'}
                rightIcon={<IconArrowRight stroke={1} />}
              />
            </VStack>
          )}
          <HStack justifyContent="center" spacing="4px">
            <Typography variant="caption">By signing up you are agreeing to</Typography>
            <Typography variant="button-small" color={XelaColor.Blue3}>
              <XLink href="#">Terms & Conditions</XLink>
            </Typography>
          </HStack>
        </VStack>
      </VStack>
    </Guest>
  )
}

export default Login
